/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useContext } from "react";
import {
  Avatar,
  Box,
  IconButton,
  LinearProgress,
  Popover,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { getPersonnelProfile } from "../../api";
import { DateTime } from "luxon";
import { AuthenticatedContext } from "../Authenticated/Authenticated";
import { AxiosError } from "axios";

interface PersonnelScreenProps {
  anchorEl: HTMLButtonElement | null;
  onClose: any;
  personnelId: number;
}

export interface PersonnelProfile {
  personnelId: number;
  avatar: string;
  forenames: string;
  surname: string;
  emailAddress: string;
  changed: string;
  birthDate: string;
  sex: string;
  nationality: string;
  employerName: string;
  jobTitle: string;
  regularlyWorksFor: string;
  occupation: string;
}

const PersonnelScreen = (props: PersonnelScreenProps) => {
  const { handleError } = useContext(AuthenticatedContext);
  const handleClose = () => {
    props.onClose(null);
  };

  const { isLoading, isFetching, data } = useQuery(
    ["personnelProfile", props.personnelId],
    () => getPersonnelProfile(props.personnelId),

    {
      refetchOnWindowFocus: false,
      onError: (err: AxiosError) => handleError(err),
    }
  );

  return (
    <Popover
      anchorEl={props.anchorEl}
      open={Boolean(props.anchorEl)}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      TransitionProps={{
        unmountOnExit: true,
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <div
        style={{
          padding: 10,
          width: 400,
          height: 500,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton onClick={handleClose}>
            <Close fontSize="inherit" />
          </IconButton>
          <Box sx={{ flexGrow: 0.43 }} />
          <Typography variant="h6">Profile</Typography>
          <div></div>
        </div>
        {isLoading || isFetching ? (
          <>
            <LinearProgress />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography>Loading</Typography>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Avatar
                sx={{
                  height: 100,
                  width: 100,
                }}
                src={`data:image/jpeg;base64,${data?.avatar}`}
                children={`${data!.forenames.charAt(0)}${data!.surname.charAt(
                  0
                )}`}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography>{data!.forenames}</Typography>
              <Typography>{data!.surname}</Typography>
              <Typography variant="subtitle1">{data!.emailAddress}</Typography>
              <Typography variant="subtitle2">
                {`Last Changed: ${data!.changed}`}
              </Typography>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">ID</Typography>
                <Typography>{props.personnelId}</Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">Date of Birth</Typography>
                <Typography>
                  {DateTime.fromFormat(
                    data!.birthDate,
                    "yyyy-LL-dd hh:mm:ss"
                  ).toFormat("dd/LL/yyyy")}
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">Sex</Typography>
                <Typography>{data!.sex}</Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">Nationality</Typography>
                <Typography>{data!.nationality}</Typography>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">Employer</Typography>
                <Typography>{data!.employerName}</Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  textAlign: "right",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">Job Title</Typography>
                <Typography>{data!.jobTitle}</Typography>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">Regularly Works For</Typography>
                <Typography>{data!.regularlyWorksFor}</Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  padding: 10,
                }}
              >
                <Typography fontWeight="bolder">Discipline</Typography>
                <Typography>{data!.occupation}</Typography>
              </div>
            </div>
          </>
        )}
      </div>
    </Popover>
  );
};

export default PersonnelScreen;
